/** Tool definitions: Messages & Notifications (sync conflicts, sync errors, option reviews). */

import { MessageType } from '../../enums/MessageType.js';
import type { ToolDefinition } from './types.js';

const MESSAGE_LIST_LIMIT_DEFAULT = 50;
const MESSAGE_LIST_LIMIT_MAX = 200;
const MESSAGE_BULK_UPDATE_MAX = 100;

export const messageTools = {
  get_messages: {
    name: 'get_messages',
    access: 'read',
    description:
      'List the user\'s Messages & Notifications (the web app\'s Settings > Messages tab): brokerage-sync transaction and income conflicts, sync errors, expired-option outcome reviews, sync-audit and data-health warnings, and info notices. Returns short summaries without the full conflict payloads, plus unreadCount and openCount. Each summary has a kind (transaction_conflict, income_conflict, expired_option_review, sync_error, sync_audit, sync_health, info), an itemCount, resolvable (true when resolve_message can fix it) and a nextStep hint. Use get_message for one message\'s full detail, resolve_message to fix the underlying issue, and update_messages to mark messages read, dismiss them or delete them.',
    inputSchema: {
      type: 'object',
      properties: {
        status: {
          type: 'string',
          enum: ['open', 'unread', 'read', 'actioned', 'dismissed', 'all'],
          default: 'open',
          description: 'Filter by status. "open" (default) is unread + read, the messages that still need attention. "all" returns every status.',
        },
        type: {
          type: 'string',
          enum: Object.values(MessageType),
          description: 'Optional message type filter.',
        },
        accountId: {
          type: 'string',
          description: 'Optional: only messages for this Luminaria account ID.',
        },
        limit: {
          type: 'integer',
          minimum: 1,
          maximum: MESSAGE_LIST_LIMIT_MAX,
          default: MESSAGE_LIST_LIMIT_DEFAULT,
          description: `Maximum messages to return, newest first (default ${MESSAGE_LIST_LIMIT_DEFAULT}, max ${MESSAGE_LIST_LIMIT_MAX}).`,
        },
      },
    },
  },
  get_message: {
    name: 'get_message',
    access: 'read',
    description:
      'Get one message with its full payload: for transaction conflicts, the saved transaction next to the broker\'s new data and the changedFields; for income conflicts, the existing and incoming income classification; for expired-option reviews, each option\'s previous and detected outcome, premium, quantity and likelyAssigned flag (finished in the money, so it was probably assigned, not expired). Also returns resolution: the item IDs, the decisions resolve_message accepts for this kind, and a nextStep hint.',
    inputSchema: {
      type: 'object',
      properties: {
        messageId: {
          type: 'string',
          description: 'Message ID from get_messages.',
        },
      },
      required: ['messageId'],
    },
  },
  resolve_message: {
    name: 'resolve_message',
    access: 'write',
    description:
      'Resolve the issue behind a message, then update the message. Resolved items are removed from it, and it is marked actioned when none are left. An item whose record changed after the message was created (resolved in the web app or through another message) is reported stale and removed without applying anything, so a change is never applied twice. Call get_message first to see the items. By kind:\n' +
      '- transaction_conflict (items are externalIds): "use_incoming" overwrites the saved transaction with the broker\'s new data, then re-derives income and lots for the account. "keep_existing" leaves the saved transaction unchanged; a later sync may report the conflict again if the broker data still differs.\n' +
      '- income_conflict (items are sourceTransactionIds): "use_incoming" applies the newly derived income classification. "keep_existing" keeps the saved income record.\n' +
      '- expired_option_review (items are incomeRecordIds): "confirm_detected" accepts each option\'s detected outcome (expired or assigned; already-closed options are only acknowledged). Options flagged likelyAssigned are skipped unless you name them in itemIds. "expire" or "assign" sets that outcome. "close" (one itemId plus closingCost) records a buy-to-close. "split" (one itemId plus portions) divides a multi-contract option into several outcomes.\n' +
      'Other kinds (sync_error, sync_audit, sync_health, info) have no automatic fix: follow the nextStep from get_message, then close them with update_messages.',
    inputSchema: {
      type: 'object',
      properties: {
        messageId: {
          type: 'string',
          description: 'Message ID from get_messages.',
        },
        decision: {
          type: 'string',
          enum: ['use_incoming', 'keep_existing', 'confirm_detected', 'expire', 'assign', 'close', 'split'],
          description: 'How to resolve the targeted items. The valid decisions depend on the message kind (see the tool description, or resolution.decisions from get_message).',
        },
        itemIds: {
          type: 'array',
          items: { type: 'string' },
          minItems: 1,
          description: 'Optional subset of the message\'s items to resolve. Defaults to every remaining item. Required (exactly one) for "close" and "split".',
        },
        closingCost: {
          type: 'number',
          exclusiveMinimum: 0,
          description: 'For "close": the total amount paid to buy the option back, as a positive number. (An option bought back for nothing expired: use "expire".)',
        },
        portions: {
          type: 'array',
          minItems: 2,
          maxItems: 10,
          description: 'For "split": the outcome of each portion of the contracts. Quantities must add up to the option\'s contract count.',
          items: {
            type: 'object',
            properties: {
              action: { type: 'string', enum: ['expire', 'close', 'assign'] },
              quantity: { type: 'integer', minimum: 1 },
              closingCost: { type: 'number', exclusiveMinimum: 0, description: 'Required when action is "close": the positive amount paid for this portion.' },
            },
            required: ['action', 'quantity'],
          },
        },
      },
      required: ['messageId', 'decision'],
    },
  },
  update_messages: {
    name: 'update_messages',
    access: 'write',
    description:
      'Change the status of one or more messages without resolving their underlying issue: "mark_read", "mark_actioned" (handled, for example after you fixed a sync error yourself), "dismiss" (hide without acting), or "delete" (permanent). mark_read only affects unread messages, dismiss only open (unread/read) ones, and already actioned messages are not changed except by delete. To apply a conflict or option resolution, use resolve_message instead.',
    inputSchema: {
      type: 'object',
      properties: {
        messageIds: {
          type: 'array',
          items: { type: 'string' },
          minItems: 1,
          maxItems: MESSAGE_BULK_UPDATE_MAX,
          description: `Message IDs from get_messages (max ${MESSAGE_BULK_UPDATE_MAX}).`,
        },
        action: {
          type: 'string',
          enum: ['mark_read', 'mark_actioned', 'dismiss', 'delete'],
          description: 'Status change to apply.',
        },
      },
      required: ['messageIds', 'action'],
    },
  },
} satisfies Record<string, ToolDefinition>;
